import { useEffect, useState, useRef, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import axiosInstance from '../api/axiosInstance';
import EmojiPicker from 'emoji-picker-react';
import AsyncSelect from 'react-select/async';
import { useMessage } from '../context/MessageContext';
import { AuthContext } from '../context/AuthContext';

function PmCenter() {
  const location = useLocation();
  const [conversations, setConversations] = useState([]);
  const [selectedConv, setSelectedConv] = useState(null);
  const [message, setMessage] = useState('');
  const [toUser, setToUser] = useState(null);
  const [subject, setSubject] = useState('');
  const [showReplyEmojiPicker, setShowReplyEmojiPicker] = useState(false);
  const [showNewEmojiPicker, setShowNewEmojiPicker] = useState(false);
  const [showNewMessageForm, setShowNewMessageForm] = useState(false);
  const [showConversationsMobile, setShowConversationsMobile] = useState(false);
  const { loadUnreadCount } = useMessage();
  const { user } = useContext(AuthContext);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    loadConversations();
    
    // URL-Parameter überprüfen
    const searchParams = new URLSearchParams(location.search);
    const compose = searchParams.get('compose');
    const toUsername = searchParams.get('to');
    
    if (compose === 'true') {
      setShowNewMessageForm(true);
      if (toUsername) {
        setToUser({ value: toUsername, label: toUsername });
      }
    }
  }, [location.search]);

  useEffect(() => {
    scrollToBottom();
  }, [selectedConv]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadConversations = async () => {
    const res = await axiosInstance.get('/pm-conversations/conversations');
    setConversations(res.data);
  };

  const loadUserOptions = async (inputValue, callback) => {
    if (inputValue.length < 3) return callback([]);
    try {
      const res = await axiosInstance.get('/users/search-usernames', {
        params: { q: inputValue },
      });
      const options = res.data.map(user => ({
        value: user.username,
        label: user.username,
      }));
      callback(options);
    } catch (err) {
      console.error('Fehler beim Laden der User:', err);
      callback([]);
    }
  };

  const loadConversation = async (convId) => {
    setShowConversationsMobile(false);
    const res = await axiosInstance.get(`/pm-conversations/conversation/${convId}`);
    setSelectedConv(res.data);
    loadUnreadCount();
    // Refresh conversations list to update unread status
    loadConversations();
  };

  const handleSendReply = async () => {
    await axiosInstance.post(
      `/pm-conversations/conversation/${selectedConv.conversation._id}/reply`,
      { content: message }
    );
    setMessage('');
    setShowReplyEmojiPicker(false);
    loadConversation(selectedConv.conversation._id);
    // Refresh conversations list to update timestamps
    loadConversations();
  };

  const handleNewMessage = async () => {
    await axiosInstance.post('/pm-conversations', {
      toUsername: toUser.value,
      subject,
      content: message,
    });
    setToUser(null);
    setSubject('');
    setMessage('');
    setShowNewEmojiPicker(false);
    setShowNewMessageForm(false);
    loadConversations();
    loadUnreadCount();
  };

  const handleMarkAllRead = async () => {
    try {
      await axiosInstance.post('/pm-conversations/mark-all-read');
      loadConversations();
      loadUnreadCount();
      // Refresh currently selected conversation if any
      if (selectedConv) {
        loadConversation(selectedConv.conversation._id);
      }
    } catch (err) {
      console.error('Fehler beim Markieren als gelesen:', err);
    }
  };

  const handleEmojiClick = (emoji) => {
    setMessage((prev) => prev + emoji.emoji);
  };

  const handleDeleteConversation = async (conversationId) => {
    if (!window.confirm('Möchten Sie diese Konversation wirklich löschen? Sie wird nur für Sie gelöscht.')) {
      return;
    }
    
    try {
      await axiosInstance.delete(`/pm-conversations/conversation/${conversationId}`);
      
      // Konversation aus Liste entfernen
      setConversations(prev => prev.filter(conv => conv._id !== conversationId));
      
      // Falls die gelöschte Konversation aktuell ausgewählt ist, Auswahl zurücksetzen
      if (selectedConv && selectedConv.conversation._id === conversationId) {
        setSelectedConv(null);
      }
      
      loadUnreadCount();
    } catch (err) {
      console.error('Fehler beim Löschen:', err);
      alert('Fehler beim Löschen der Konversation');
    }
  };

  return (
    <div className="max-w-5xl mx-auto p-4 grid grid-cols-1 md:grid-cols-3 gap-4">
      {/* Mobile Accordion */}
      <div className="md:hidden mb-2">
        <button
          className="btn btn-block text-white"
          style={{ backgroundColor: '#448f65' }}
          onClick={() => setShowConversationsMobile(!showConversationsMobile)}
        >
          📬 Konversationen {showConversationsMobile ? 'verbergen' : 'anzeigen'}
        </button>
        {showConversationsMobile && (
          <div className="mt-2 bg-base-200 rounded shadow p-4">
            {conversations.map((conv) => (
              <div
                key={conv._id}
                onClick={() => loadConversation(conv._id)}
                className="p-3 hover:bg-base-300 cursor-pointer rounded mb-2 transition-all"
              >
                <div className="flex justify-between items-center mb-1">
                  <div className="font-bold">
                    {conv.subject}
                  </div>
                  {conv.hasUnread && (
                    <span className="badge badge-error text-white text-xs">
                      {conv.unreadCount || 'neu'}
                    </span>
                  )}
                </div>
                <div className="text-xs text-gray-500">
                  mit {conv.participants.filter(p => p).map((p) => p.username).join(', ')}
                </div>
                <div className="text-xs text-gray-400 mt-1">
                  {new Date(conv.updatedAt).toLocaleString()}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Linke Spalte: Konversationen */}
      <div className="col-span-1 bg-base-200 rounded shadow p-4 hidden md:block">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-[#448f65]">Meine Nachrichten</h2>
          <div className="flex items-center gap-2">
            {conversations.some(conv => conv.hasUnread) && (
              <>
                <button
                  onClick={handleMarkAllRead}
                  className="btn btn-xs btn-outline btn-success"
                  title="Alle als gelesen markieren"
                >
                  ✓ Alle lesen
                </button>
                <div className="flex items-center gap-1 text-xs text-gray-600">
                  <span>Neue Nachrichten</span>
                </div>
              </>
            )}
          </div>
        </div>
        {conversations.length === 0 ? (
          <div className="text-center text-gray-500 py-8">
            <p>Keine Unterhaltungen vorhanden</p>
            <p className="text-xs mt-2">Starten Sie eine neue Unterhaltung!</p>
          </div>
        ) : (
          conversations.map((conv) => (
            <div
              key={conv._id}
              className="p-3 hover:bg-base-300 cursor-pointer rounded mb-2 transition-all relative"
            >
              <div onClick={() => loadConversation(conv._id)}>
                <div className="flex justify-between items-center mb-1 pr-6">
                  <div className="font-bold">
                    {conv.subject}
                  </div>
                  {conv.hasUnread && (
                    <span className="badge badge-error text-white text-xs">
                      {conv.unreadCount || 'neu'}
                    </span>
                  )}
                </div>
                <div className="text-xs text-gray-500">
                  mit {conv.participants.filter(p => p).map((p) => p.username).join(', ')}
                </div>
                <div className="text-xs text-gray-400 mt-1">
                  {new Date(conv.updatedAt).toLocaleString()}
                </div>
              </div>
              {/* Löschbutton */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteConversation(conv._id);
                }}
                className="absolute top-2 right-2 btn btn-xs btn-ghost text-red-500 hover:bg-red-100 opacity-60 hover:opacity-100"
                title="Konversation löschen"
              >
                ✕
              </button>
            </div>
          ))
        )}
      </div>

      {/* Mittlere Spalte: Nachrichtenverlauf */}
      <div className="col-span-3 md:col-span-2 bg-base-200 rounded shadow p-4 flex flex-col">
        {selectedConv ? (
          <>
            <h2 className="text-xl mb-2 font-bold text-[#448f65]">
              {selectedConv.conversation.subject}
            </h2>
            <div className="space-y-2 max-h-80 overflow-y-auto mb-4 flex flex-col">
              {selectedConv.messages.map((msg) => {
                const isOwn = msg.from.username === user.username;
                const isUnread = !msg.read && msg.to._id === user.id;
                return (
                  <div
                    key={msg._id}
                    className={`p-3 rounded transition-all ${
                      isOwn ? 'self-start bg-neutral text-neutral-content' : 'self-end bg-base-100'
                    } max-w-[70%] relative`}
                  >
                    <div className="text-sm font-semibold flex items-center gap-2 mb-1">
                      {msg.from.avatarUrl && (
                        <img
                          src={`https://forumapi.more-features.com${msg.from.avatarUrl}`}
                          alt="avatar"
                          className="w-8 h-8 rounded-full"
                        />
                      )}
                      <span>
                        {msg.from.username}
                      </span>
                      <span className="text-xs text-gray-400">
                        {new Date(msg.createdAt).toLocaleString()}
                      </span>
                      {isUnread && !isOwn && (
                        <span className="text-xs bg-red-500 text-white px-1 rounded">NEU</span>
                      )}
                    </div>
                    <div
                      className="prose text-sm"
                      dangerouslySetInnerHTML={{ __html: msg.contentHtml }}
                    />
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="textarea textarea-bordered w-full mb-2"
              rows={4}
              placeholder="Antwort schreiben..."
            />
            {showReplyEmojiPicker && (
              <EmojiPicker onEmojiClick={(e) => handleEmojiClick(e)} />
            )}
            <div className="flex gap-2">
              <button
                onClick={() => setShowReplyEmojiPicker(!showReplyEmojiPicker)}
                className="btn btn-sm text-white"
                style={{ backgroundColor: '#448f65' }}
              >
                😊 Emoji
              </button>
              <button
                onClick={handleSendReply}
                className="btn btn-sm text-white"
                style={{ backgroundColor: '#448f65' }}
              >
                Antwort senden
              </button>
            </div>
          </>
        ) : (
          <div className="text-gray-500">
            Wähle eine Konversation aus oder schreibe eine neue Nachricht.
          </div>
        )}
      </div>

      {/* Neue Nachricht: Accordion */}
      <div className="col-span-3 bg-base-200 rounded shadow p-4">
        <button
          className="btn mb-2 text-white"
          style={{ backgroundColor: '#448f65' }}
          onClick={() => setShowNewMessageForm((f) => !f)}
        >
          ✉️ Neue Nachricht
        </button>

        {showNewMessageForm && (
          <div className="space-y-3">
            <AsyncSelect
              classNamePrefix="react-select"
              styles={{
                control: (base, state) => ({
                  ...base,
                  borderColor: '#448f65',
                  backgroundColor: '#ffffff',
                  boxShadow: state.isFocused ? '0 0 0 1px #448f65' : undefined,
                  '&:hover': {
                    borderColor: '#448f65',
                  },
                }),
              }}
              cacheOptions
              loadOptions={loadUserOptions}
              defaultOptions={false}
              value={toUser}
              onChange={setToUser}
              placeholder="Empfänger eingeben…"
              noOptionsMessage={() => 'Keine Treffer'}
              loadingMessage={() => 'Suche...'}
            />
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Betreff"
              className="input input-bordered w-full"
            />
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="textarea textarea-bordered w-full"
              rows={4}
              placeholder="Nachricht"
            />
            {showNewEmojiPicker && (
              <EmojiPicker onEmojiClick={(e) => handleEmojiClick(e)} />
            )}
            <div className="flex gap-2">
              <button
                onClick={() => setShowNewEmojiPicker(!showNewEmojiPicker)}
                className="btn btn-sm text-white"
                style={{ backgroundColor: '#448f65' }}
              >
                😊 Emoji
              </button>
              <button
                onClick={handleNewMessage}
                className="btn btn-sm text-white"
                style={{ backgroundColor: '#448f65' }}
              >
                Senden
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default PmCenter;
